import { normalizeTime } from '../../utils/helpers';

const MINUTES_PER_DAY = 24 * 60;

/** HH:MM to minutes past midnight, or null when absent/malformed. */
export const timeToMinutes = (time) => {
  const normalized = normalizeTime(time);
  if (!normalized) return null;
  const [h, m] = normalized.split(':').map(Number);
  return h * 60 + m;
};

/**
 * Minutes elapsed from `anchorMins` to `mins`, walking forward through
 * midnight. Shifts routinely span midnight, so comparing raw
 * minutes-past-midnight would rank 01:00 before 22:00. Re-basing every time
 * onto the shift start turns the window into a monotonic 0..1439 line where
 * plain `<` means "earlier in the shift".
 */
export const toShiftOffset = (mins, anchorMins) =>
  (((mins - anchorMins) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;

/** True when `time` falls inside the ops clock window (inclusive, midnight aware). */
export const isTimeInBounds = (time, bounds) => {
  if (!time || !bounds) return true;
  const mins = timeToMinutes(time);
  if (mins === null) return true; // format errors are reported separately
  if (bounds.minMins <= bounds.maxMins) {
    return mins >= bounds.minMins && mins <= bounds.maxMins;
  }
  return mins >= bounds.minMins || mins <= bounds.maxMins;
};

/**
 * The outer envelope of the ops clock: the earliest start and latest end across
 * every process in every cycle.
 *
 * This has to span all cycles, not just CYCLE_1. Period derivation takes the
 * precharge start from the earliest INDUCT/STOW across *all* cycles, so a
 * CYCLE_1-only window would reject the very periods the ops clock implies — a
 * station whose earlier cycle starts at 23:50 could never produce a valid
 * auto-filled set against a 01:20 lower bound.
 *
 * @param {Array} clockEvents ops clock events
 * @param {(epochSeconds:number)=>string} toHHMM station-local epoch → "HH:MM"
 * @param {(epochSeconds:number)=>number} toMins station-local epoch → minutes past midnight
 * @returns {Bounds|null} null when there are no usable events
 */
export const computeOpsClockBounds = (clockEvents, toHHMM, toMins) => {
  const times = (clockEvents || [])
    .map((e) => e.timeIntervals?.[0])
    .filter((ti) => ti && typeof ti.startTime === 'number' && typeof ti.endTime === 'number');
  if (times.length === 0) return null;

  const earliest = times.reduce((min, ti) => Math.min(min, ti.startTime), Infinity);
  const latest = times.reduce((max, ti) => Math.max(max, ti.endTime), -Infinity);
  return {
    min: toHHMM(earliest),
    max: toHHMM(latest),
    minMins: toMins(earliest),
    maxMins: toMins(latest)
  };
};

/**
 * Derive a set of strictly continuous periods from an ops clock, for the
 * "auto-fill suggested periods" action.
 */
export const deriveIntervalsFromOpsClock = (clockEvents, breaks, toHHMM) => {
  if (!Array.isArray(clockEvents)) return [];
  const cycle1 = (process) =>
    clockEvents.find((e) => e.process === process && e.cycleName === 'CYCLE_1')?.timeIntervals?.[0];
  const induct = cycle1('INDUCT');
  const stow = cycle1('STOW');
  const pick = cycle1('PICK');
  if (!induct || !stow || !pick) return [];

  // Same-day breaks only (offset 0), in chronological order: Break 1, Lunch, Break 2.
  const sameDayBreaks = (breaks || [])
    .filter((b) => b.offsetFromDispatchDate === 0 && b.timeIntervals?.[0])
    .sort((a, b) => a.timeIntervals[0].startTime - b.timeIntervals[0].startTime);
  const [break1, lunch] = sameDayBreaks;

  // Precharge start: the earliest INDUCT/STOW start across every cycle, matching
  // the flow branch. The shift begins here when it precedes CYCLE_1 stow.
  const earliestStart = clockEvents
    .filter((e) => ['INDUCT', 'STOW'].includes(e.process) && e.timeIntervals?.[0])
    .reduce((min, e) => Math.min(min, e.timeIntervals[0].startTime), Infinity);
  const prechargeStart = Math.min(earliestStart, induct.startTime);

  const shiftStart = Math.min(prechargeStart, stow.startTime);
  const shiftEnd = pick.endTime;

  // Interior boundaries mirror the flow period edges. Break ends split the early
  // shift; induct/stow ends split the late shift. Anything at or outside the
  // shift envelope is discarded, then the ends are added back explicitly.
  const interior = [
    stow.startTime,
    break1?.timeIntervals[0].endTime,
    lunch?.timeIntervals[0].endTime,
    induct.endTime,
    stow.endTime
  ].filter((t) => typeof t === 'number' && t > shiftStart && t < shiftEnd);

  const boundaries = [...new Set([shiftStart, ...interior, shiftEnd])].sort((a, b) => a - b);
  if (boundaries.length < 2) return [];

  const intervals = [];
  for (let i = 0; i < boundaries.length - 1; i++) {
    intervals.push({
      windowName: `P${i + 1}`,
      startTime: toHHMM(boundaries[i]),
      endTime: toHHMM(boundaries[i + 1])
    });
  }
  return intervals;
};

/**
 * Overlay the typed overrides onto the saved config to get the intervals that
 * would actually be written. Malformed input falls back to the saved value;
 * `validateTimeDistributions` reports the format error before continuity is
 * ever considered, so the fallback can't mask a bad entry.
 */
export const getEffectiveIntervals = (intervals, savedIntervals) =>
  intervals.map((interval) => {
    const saved = savedIntervals ? savedIntervals.find((s) => s.windowName === interval.windowName) : null;
    return {
      windowName: interval.windowName,
      startTime: normalizeTime(interval.startTime) || (saved ? saved.startTime : ''),
      endTime: normalizeTime(interval.endTime) || (saved ? saved.endTime : '')
    };
  });

/**
 * Validate a pending edit of the time distributions.
 *
 * @param {Interval[]} intervals      Raw editor rows (overrides as typed)
 * @param {Interval[]|null} savedIntervals Currently persisted config
 * @param {Bounds|null} bounds        Ops clock window, when available
 * @returns {ValidationResult}
 */
export const validateTimeDistributions = (intervals, savedIntervals, bounds) => {
  const messages = [];
  const fieldErrors = {};
  const flag = (index, field) => {
    fieldErrors[`${index}:${field}`] = true;
  };

  // 1. Format. Checked against the raw entries so we blame the row the user
  //    actually typed into, and short circuited because every later rule needs
  //    parseable times.
  intervals.forEach((interval, index) => {
    ['startTime', 'endTime'].forEach((field) => {
      if (interval[field] !== '' && normalizeTime(interval[field]) === null) flag(index, field);
    });
  });
  if (Object.keys(fieldErrors).length > 0) {
    return { valid: false, messages: ['Times must be in HH:MM format.'], fieldErrors };
  }

  if (intervals.length === 0) {
    return { valid: false, messages: ['At least one period is required.'], fieldErrors };
  }

  const effective = getEffectiveIntervals(intervals, savedIntervals);

  // 2. Completeness. A period missing either end can't be placed on the
  //    timeline, so continuity is unknowable until it's filled in.
  effective.forEach((interval, index) => {
    if (!interval.startTime) flag(index, 'startTime');
    if (!interval.endTime) flag(index, 'endTime');
    if (!interval.startTime || !interval.endTime) {
      messages.push(`${interval.windowName} needs both a start and an end time.`);
    }
  });
  if (messages.length > 0) return { valid: false, messages, fieldErrors };

  // 3. Ops clock window
  if (bounds) {
    effective.forEach((interval, index) => {
      let outOfBounds = false;
      ['startTime', 'endTime'].forEach((field) => {
        if (!isTimeInBounds(interval[field], bounds)) {
          flag(index, field);
          outOfBounds = true;
        }
      });
      if (outOfBounds) {
        messages.push(`${interval.windowName} must be within the valid range ${bounds.min} - ${bounds.max}.`);
      }
    });
    if (messages.length > 0) return { valid: false, messages, fieldErrors };
  }

  // Everything below is ordering, which is only meaningful on a shared
  // timeline. Anchor on the shift start when we know it, else on the first
  // period's start.
  const anchorMins = bounds ? bounds.minMins : timeToMinutes(effective[0].startTime);
  const offset = (time) => toShiftOffset(timeToMinutes(time), anchorMins);

  // 4. Each period must move forward in time.
  effective.forEach((interval, index) => {
    if (offset(interval.startTime) >= offset(interval.endTime)) {
      flag(index, 'startTime');
      flag(index, 'endTime');
      messages.push(
        `${interval.windowName} start (${interval.startTime}) must be before its end (${interval.endTime}).`
      );
    }
  });
  if (messages.length > 0) return { valid: false, messages, fieldErrors };

  // 5. Continuity — the rule this module exists for. Adjacent periods must
  //    meet exactly: one period's end is the next one's start.
  for (let i = 0; i < effective.length - 1; i++) {
    const current = effective[i];
    const next = effective[i + 1];
    if (current.endTime === next.startTime) continue;

    flag(i, 'endTime');
    flag(i + 1, 'startTime');
    // Compare relative to this seam rather than to the shift start. A time that
    // precedes the anchor wraps to the far end of the day, which would invert
    // the gap/overlap verdict for out-of-range rows; measuring the next start
    // as an offset from the current end is anchor-independent. Anything in the
    // second half of the day is read as running backwards, i.e. an overlap.
    const delta = toShiftOffset(timeToMinutes(next.startTime), timeToMinutes(current.endTime));
    const isGap = delta < MINUTES_PER_DAY / 2;
    messages.push(
      `${isGap ? 'Gap' : 'Overlap'} between ${current.windowName} and ${next.windowName}: ` +
        `${current.windowName} ends at ${current.endTime} but ${next.windowName} starts at ${next.startTime}. ` +
        'Periods must be continuous.'
    );
  }

  return { valid: messages.length === 0, messages, fieldErrors };
};
